"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.LastModifiedTimeField = void 0;
const joi_1 = __importDefault(require("joi"));
const field_types_1 = require("../../types/field_types");
const datasheet_1 = require("../datasheet");
const date_time_base_field_1 = require("./date_time_base_field");
const validate_schema_1 = require("./validate_schema");
class LastModifiedTimeField extends date_time_base_field_1.DateTimeBaseField {
    constructor(field, state) {
        super(field, state);
        this.field = field;
    }
    static createDefault(fieldMap) {
        return {
            id: datasheet_1.DatasheetActions.getNewFieldId(fieldMap),
            type: field_types_1.FieldType.LastModifiedTime,
            name: datasheet_1.DatasheetActions.getDefaultFieldName(fieldMap),
            property: this.defaultProperty(),
        };
    }
    static defaultProperty() {
        return {
            dateFormat: field_types_1.DateFormat['YYYY/MM/DD'],
            timeFormat: field_types_1.TimeFormat['hh:mm'],
            includeTime: false,
            collectType: field_types_1.CollectType.AllFields,
            fieldIdCollection: [],
            datasheetId: '',
        };
    }
    get isComputed() {
        return true;
    }
    recordEditable() {
        return false;
    }
    validateProperty() {
        return LastModifiedTimeField.propertySchema.validate(this.field.property);
    }
    validateCellValue() {
        return (0, validate_schema_1.joiErrorResult)("computed field shouldn't validate cellValue");
    }
    validateOpenWriteValue() {
        return (0, validate_schema_1.joiErrorResult)("computed field shouldn't validate cellValue");
    }
    stdValueToCellValue() {
        return null;
    }
    getCellValue(record) {
        var _a;
        const { collectType, fieldIdCollection } = this.field.property;
        const updatedMap = (_a = record.recordMeta) === null || _a === void 0 ? void 0 : _a.fieldUpdatedMap;
        // Depends on fieldUpdatedMap, otherwise returns null
        if (!updatedMap) {
            return null;
        }
        const isAllField = collectType === field_types_1.CollectType.AllFields;
        const fieldIds = isAllField ? Object.keys(updatedMap) : fieldIdCollection;
        const timestamps = fieldIds.reduce((acc, fieldId) => {
            var _a;
            if ((_a = updatedMap[fieldId]) === null || _a === void 0 ? void 0 : _a.at) {
                acc.push(updatedMap[fieldId].at);
            }
            return acc;
        }, []);
        return timestamps.length ? Math.max(...timestamps) : null;
    }
    get openFieldProperty() {
        const { includeTime, dateFormat, timeFormat, collectType, fieldIdCollection } = this.field.property;
        return {
            dateFormat: field_types_1.DateFormat[dateFormat],
            timeFormat: field_types_1.TimeFormat[timeFormat],
            includeTime,
            collectType,
            fieldIdCollection
        };
    }
    validateUpdateOpenProperty(updateProperty) {
        return LastModifiedTimeField.openUpdatePropertySchema.validate(updateProperty);
    }
    updateOpenFieldPropertyTransformProperty(openFieldProperty) {
        const { dateFormat, timeFormat, includeTime, collectType, fieldIdCollection } = openFieldProperty;
        const defaultProperty = LastModifiedTimeField.defaultProperty();
        return {
            datasheetId: this.field.property.datasheetId,
            dateFormat: field_types_1.DateFormat[dateFormat],
            timeFormat: timeFormat ? field_types_1.TimeFormat[timeFormat] : defaultProperty.timeFormat,
            collectType: collectType !== null && collectType !== void 0 ? collectType : defaultProperty.collectType,
            fieldIdCollection: fieldIdCollection || [],
            includeTime: includeTime !== null && includeTime !== void 0 ? includeTime : defaultProperty.includeTime
        };
    }
}
exports.LastModifiedTimeField = LastModifiedTimeField;
LastModifiedTimeField.propertySchema = joi_1.default.object({
    datasheetId: (0, validate_schema_1.datasheetIdString)().required(),
    collectType: joi_1.default.number().allow(field_types_1.CollectType.AllFields, field_types_1.CollectType.SpecifiedFields).required(),
    fieldIdCollection: joi_1.default.array().items(joi_1.default.string()).required(),
    dateFormat: joi_1.default.string().allow(...(0, validate_schema_1.enumToArray)(field_types_1.DateFormat)).required(),
    timeFormat: joi_1.default.string().allow(...(0, validate_schema_1.enumToArray)(field_types_1.TimeFormat)).required(),
    includeTime: joi_1.default.boolean().required(),
    timeZone: joi_1.default.string(),
    includeTimeZone: joi_1.default.boolean(),
}).required();
LastModifiedTimeField.defaultDateFormat = field_types_1.DateFormat[0];
LastModifiedTimeField.defaultTimeFormat = field_types_1.TimeFormat[0];
LastModifiedTimeField.openUpdatePropertySchema = joi_1.default.object({
    dateFormat: joi_1.default.string().valid(...(0, validate_schema_1.enumKeyToArray)(field_types_1.DateFormat)).required(),
    timeFormat: joi_1.default.valid(...(0, validate_schema_1.enumKeyToArray)(field_types_1.TimeFormat)),
    includeTime: joi_1.default.boolean(),
    collectType: joi_1.default.number().valid(field_types_1.CollectType.AllFields, field_types_1.CollectType.SpecifiedFields),
    fieldIdCollection: joi_1.default.array().items(joi_1.default.string()),
    timeZone: joi_1.default.string(),
    includeTimeZone: joi_1.default.boolean(),
}).required();
//# sourceMappingURL=last_modified_time_field.js.map